import LocationLanding from './LocationLanding';
import { buildBreadcrumb, buildFAQPage, SITE_URL } from '@/lib/seo';

const SLUG = 'celine-ambulance-diou';
const CANONICAL = `${SITE_URL}/${SLUG}`;

const COMMUNES = [
  { name: 'Diou',                    lat: 46.5333, lng: 3.7333, main: true },
  { name: 'Dompierre-sur-Besbre',    lat: 46.5225, lng: 3.6836, main: true },
  { name: 'Saligny-sur-Roudon',      lat: 46.5556, lng: 3.7822, main: true },
  { name: 'Coulanges',               lat: 46.5644, lng: 3.7061 },
  { name: 'Pierrefitte-sur-Loire',   lat: 46.4906, lng: 3.7561 },
  { name: 'Molinet',                 lat: 46.4508, lng: 3.9558 },
  { name: 'Jaligny-sur-Besbre',      lat: 46.3822, lng: 3.6236 },
  { name: 'Beaulon',                 lat: 46.5978, lng: 3.7803 },
];

const FAQS = [
  { q: 'Comment réserver un transport sanitaire avec Céline Ambulance à Diou ?', a: `Appelez directement notre équipe au 04 70 42 90 08 ou remplissez le formulaire ci-dessus. Nous vous rappelons pour confirmer les horaires et les modalités.` },
  { q: 'Quelles communes desservez-vous depuis Diou ?', a: `Nous couvrons principalement Diou, Dompierre-sur-Besbre, Saligny-sur-Roudon, Beaulon, Coulanges, Pierrefitte-sur-Loire, Molinet et Jaligny-sur-Besbre. Nous intervenons plus largement sur tout le Val de Besbre et le nord-est de l\'Allier selon disponibilité.` },
  { q: 'Vos véhicules sont-ils conventionnés par l\'Assurance Maladie ?', a: `Oui. Toutes nos ambulances et nos VSL sont conventionnés CPAM. Nous appliquons la prise en charge directe (tiers payant) pour les patients éligibles.` },
  { q: 'Quelle est la différence entre une ambulance et un VSL ?', a: `L\'ambulance est utilisée lorsque le patient doit être transporté en position allongée ou nécessite une surveillance médicale. Le VSL (Véhicule Sanitaire Léger) est adapté aux patients pouvant voyager en position assise mais nécessitant une assistance. Le choix est indiqué par votre médecin sur la prescription.` },
  { q: 'Une prescription médicale est-elle obligatoire pour un remboursement CPAM ?', a: `Oui. Une prescription médicale (bon de transport CERFA n°50742*04) délivrée par votre médecin est obligatoire pour un remboursement, sauf en cas d\'urgence vitale.` },
  { q: 'Quels services proposez-vous ?', a: `Transport en ambulance et en VSL pour hospitalisations, consultations, dialyses, chimiothérapie, radiothérapie, rééducation, sorties d\'hôpital, transferts inter-établissements et longue distance.` },
];

const CONFIG = {
  slug: SLUG,
  brandName: 'Céline Ambulance – Diou',
  brandNameShort: 'Céline Ambulance',
  brandTitle: 'Céline Ambulance',
  brandSubtitle: 'Diou · Val de Besbre',
  kicker: 'Notre implantation dans le Val de Besbre',
  zoneLabel: 'Diou · Dompierre-sur-Besbre',
  zoneLabelShort: 'Diou / Val de Besbre',
  heroTitle: (<>Ambulance et VSL à <span className="text-brand-700">Diou</span>, <span className="text-brand-700">Dompierre-sur-Besbre</span> et <span className="text-brand-700">Saligny-sur-Roudon</span></>),
  description: (<>Transport sanitaire conventionné pour <strong className="text-anthracite-900">consultations, hospitalisations, dialyses, rééducation, sorties d'hôpital</strong> et transports programmés dans le Val de Besbre.</>),
  phoneDisplay: '04 70 42 90 08',
  phoneTel: '+33470429008',
  whyChooseTitle: 'Une équipe locale, au cœur du Val de Besbre',
  serviceAreaTitle: 'Nous intervenons sur tout le secteur Diou / Val de Besbre',
  serviceAreaDesc: 'Diou, Dompierre-sur-Besbre, Saligny-sur-Roudon, Beaulon, Coulanges, Pierrefitte-sur-Loire, Molinet, Jaligny-sur-Besbre et communes environnantes. Nous intervenons plus largement dans tout le département de l\'Allier selon disponibilité.',
  communes: COMMUNES,
  mapCenter: [46.5333, 3.7333],
  mapRadius: 12000,
  mapZoom: 11,
  canonical: CANONICAL,
  ogImage: 'https://customer-assets.emergentagent.com/job_ambulances-beaulon/artifacts/5zctf2x4_file_000000005b88720a8aa856385a7438f6.png',
  ogType: 'website',
  seoTitle: 'Ambulance Diou & Dompierre-sur-Besbre | Céline Ambulance',
  seoDescription: 'Ambulance et VSL à Diou, Dompierre-sur-Besbre, Saligny-sur-Roudon. Transport sanitaire conventionné CPAM dans le Val de Besbre. Réservez au 04 70 42 90 08.',
  seoKeywords: 'ambulance diou, celine ambulance, ambulance dompierre-sur-besbre, ambulance saligny-sur-roudon, vsl diou, vsl val de besbre, transport sanitaire diou, transport sanitaire dompierre, ambulance conventionnée diou, ambulance jaligny, ambulance molinet, transport dialyse val de besbre',
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': ['MedicalBusiness', 'LocalBusiness'],
      '@id': `${CANONICAL}#business`,
      name: 'Céline Ambulance – Diou',
      alternateName: ['Céline Ambulance Dompierre', 'Ambulance Diou'],
      description: 'Transport sanitaire conventionné en ambulance et VSL à Diou, Dompierre-sur-Besbre, Saligny-sur-Roudon et dans le Val de Besbre.',
      url: CANONICAL,
      telephone: '+33470429008',
      priceRange: '€€',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Diou',
        postalCode: '03290',
        addressRegion: 'Allier',
        addressCountry: 'FR',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 46.5333, longitude: 3.7333 },
      areaServed: COMMUNES.map((c) => ({ '@type': 'City', name: c.name })),
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
        opens: '00:00', closes: '23:59',
      }],
      parentOrganization: { '@type': 'Organization', name: 'Groupe Ambulances Beaulon', url: SITE_URL },
      medicalSpecialty: 'MedicalTransport',
    },
    buildBreadcrumb([
      { name: 'Accueil', url: SITE_URL + '/' },
      { name: 'Nos implantations', url: SITE_URL + '/implantations' },
      { name: 'Céline Ambulance – Diou', url: CANONICAL },
    ]),
    buildFAQPage(FAQS),
  ],
};

export default function CelineAmbulanceDiou() {
  return <LocationLanding config={CONFIG} />;
}
